import IconFlag from "@/icons/IconFlag";
import IconPlus from "@/icons/IconPlus";
import React, {
  DragEventHandler,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { Action, NodeColors, NodeType, actionDefs } from "../types";
import IconCopy from "@/icons/IconCopy";
import IconTrash2 from "@/icons/IconTrash2";
import { DraggingData } from "../WorkflowsEditor";
import useSubscriptions from "@/hooks/useSubscriptions";
import useAdminFeatures from "@/hooks/useAdminFeatures";
interface NodeProps {
  icon: React.ReactNode;
  title: string;
  text?: string;
  type: NodeType;
  action: Action;
  selected: boolean;
  name?: string;
  hasOptions: boolean;
  nodeColors: NodeColors;
  channelBadge?: string;
  onSelect: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onDragStart: DragEventHandler<HTMLDivElement>;
  onDragEnd: DragEventHandler<HTMLDivElement>;
}

export function Node({
  icon,
  title,
  text,
  type,
  action,
  selected,
  name,
  hasOptions,
  nodeColors,
  channelBadge,
  onSelect,
  onDelete,
  onCopy,
  onDragStart,
  onDragEnd
}: NodeProps) {
  // const textColor = text ? "text-slate-500 font-bold" : "text-slate-300";
  const textColor = !text
    ? "text-slate-300"
    : type === "trigger"
    ? "text-slate-800 font-bold"
    : type === "action"
    ? "text-slate-800 font-normal"
    : "";

  let borderColor = nodeColors.borderColor;
  let shadowStyle = "shadow-lg";

  if (!selected) {
    borderColor = " border-slate-200";
    shadowStyle = "shadow";
  }

  return (
    <div className="relative">
      <div
        draggable={true}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onSelect}
        id="node"
        className="flex flex-col cursor-pointer relative"
      >
        <div
          className={`w-64 h-12 p-4 ${nodeColors.bgColor} rounded-tl-2xl rounded-tr-2xl flex-col justify-center items-center gap-2 inline-flex`}
        >
          <div className="self-stretch h-full justify-start items-center gap-1.5 inline-flex">
            <div className="w-6 h-6 relative">{icon}</div>
            <div className="text-white text-sm font-medium leading-normal overflow-hidden whitespace-nowrap text-ellipsis">
              {name || title}
            </div>
          </div>
        </div>
        <div
          className={`w-64 h-14 p-2 bg-white rounded-bl-2xl rounded-br-2xl border ${borderColor} ${shadowStyle} ${textColor} flex-col justify-center items-center inline-flex`}
        >
          <div
            className={`${textColor} self-stretch inline-flex overflow-hidden text-sm leading-tight h-min max-h-9`}
          >
            {text}
          </div>
        </div>
        {channelBadge && (
          <div
            className="absolute -right-2 -top-2 px-1.5 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full shadow-sm z-10 whitespace-nowrap"
            title={`Channel-specific behavior for ${channelBadge}`}
          >
            {channelBadge}
          </div>
        )}
        {selected && (
          <div
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute -left-7 top-1/2 -translate-y-1/2 cursor-grab"
          >
            <DragDots />
          </div>
        )}
      </div>
      {selected && type !== "trigger" && (
        <NodeActions>
          {!hasOptions && (
            <div
              onClick={onCopy}
              className="w-10 h-10 p-2 bg-slate-50 rounded-lg shadow border border-slate-200 cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors group flex items-center justify-center"
            >
              <IconCopy className="text-slate-500 group-hover:text-blue-600 transition-colors" />
            </div>
          )}
          <div
            onClick={onDelete}
            className="w-10 h-10 p-2 bg-slate-50 rounded-lg shadow border border-slate-200 cursor-pointer hover:bg-red-50 hover:border-red-400 transition-colors group flex items-center justify-center"
          >
            <IconTrash2 className="text-slate-500 group-hover:text-red-600 transition-colors" />
          </div>
        </NodeActions>
      )}
    </div>
  );
}

export function EndNode() {
  return (
    <div className="w-64 h-10 p-4 bg-slate-100 rounded-2xl border border-slate-200 justify-start items-center gap-2 flex">
      <IconFlag />
      <span className="text-slate-400 text-sm font-medium">
        End of Workflow
      </span>
    </div>
  );
}

export function Connector({
  selectedConnector,
  draggingData,
  onClick,
  onDragEnter,
  onDragLeave
}: {
  selectedConnector?: HTMLDivElement;
  draggingData: DraggingData | null;
  onClick: (el: HTMLDivElement) => void;
  onDragEnter: (el: HTMLDivElement) => void;
  onDragLeave: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const focused = selectedConnector === ref.current;
  const borderColor = focused
    ? "border-cornflower-blue-500"
    : "border-slate-300";
  const textColor = focused ? "text-cornflower-blue-500" : "text-slate-300";
  const draggedOver = draggingData?.draggedOverConnector?.el === ref.current;
  const draggingColor = draggedOver ? "bg-cornflower-blue-100" : "bg-white";
  return (
    <div ref={ref} className="w-8 h-32 flex flex-col">
      <div className="relative -left-[2px] translate-x-1/2 flex-grow border-l-4 border-slate-300"></div>
      {draggingData && (
        <div
          onDragEnter={() => {
            if (!ref.current) {
              return;
            }
            onDragEnter(ref.current);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={onDragLeave}
          className={`w-8 h-8 border-2 border-dashed rounded-full border-cornflower-blue-500 flex items-center justify-center shadow cursor-pointer ${draggingColor} relative`}
        ></div>
      )}
      {!draggingData && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (!ref.current) {
              return;
            }
            onClick(ref.current);
          }}
          className={`w-8 h-8 border-2 rounded-full ${borderColor} 
            flex items-center justify-center shadow cursor-pointer
          bg-white relative hover:bg-slate-50 hover:border-slate-500 
            transition-colors duration-300 hover:shadow-lg`}
        >
          <IconPlus
            className={`${textColor} hover:text-slate-500 transition-colors duration-300`}
          />
        </div>
      )}
      <div className="relative -left-[2px] translate-x-1/2 flex-grow border-l-4 border-slate-300"></div>
    </div>
  );
}

/** Grouping definition for the Add Action menu. */
const ACTION_GROUPS: { label: string; actions: Action[] }[] = [
  {
    label: "Messaging",
    actions: ["sendMessage", "askQuestion"]
  },
  {
    label: "Email",
    actions: ["aiComposeEmail", "sendEmail"]
  },
  {
    label: "SMS & RCS",
    actions: ["sendSms", "sendRcs"]
  },
  {
    label: "Logic",
    actions: ["condition", "jumpTo", "workflowSection", "callWorkflow"]
  },
  {
    label: "Data",
    actions: ["setData", "httpRequest", "pushToCrm"]
  },
  {
    label: "AI",
    actions: ["transferToAI", "classifyAndRoute", "netsuiteAI", "dynamicsAI", "hubSpotAI", "bigCommerceAI"]
  },
  {
    label: "Scheduling & Payments",
    actions: ["bookAppointment", "sendCalendlyLink", "requestPayment"]
  },
  {
    label: "Control",
    actions: ["transferToAgents", "timeDelay", "postConversation", "closeConversation"]
  }
];

export function ActionMenu({
  onSelect
}: {
  onSelect: (action: Action) => void;
}) {
  const { subscription } = useSubscriptions();
  const { enableDynamics, enableHubSpot, enableBigCommerce } = useAdminFeatures();
  const ref = useRef<HTMLDivElement>(null);

  // Drag state — offset applied via CSS transform so the parent's fixed position stays unchanged
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dragOriginRef = useRef<{ mx: number; my: number; ox: number; oy: number } | null>(null);

  // Clamp to viewport after initial render (handles menus near bottom / right edge)
  useLayoutEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (rect.bottom > window.innerHeight - 8) dy = window.innerHeight - 8 - rect.bottom;
    if (rect.right > window.innerWidth - 8) dx = window.innerWidth - 8 - rect.right;
    if (rect.left < 8) dx = 8 - rect.left;
    if (rect.top < 8) dy = 8 - rect.top;
    if (dx !== 0 || dy !== 0) setDragOffset({ x: dx, y: dy });
  }, []);

  // Block wheel events from propagating to the canvas pan handler
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const onWheel = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", onWheel);
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  function handleDragHandleMouseDown(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragOriginRef.current = {
      mx: e.clientX,
      my: e.clientY,
      ox: dragOffset.x,
      oy: dragOffset.y
    };

    function onMove(ev: MouseEvent) {
      if (!dragOriginRef.current) return;
      setDragOffset({
        x: dragOriginRef.current.ox + ev.clientX - dragOriginRef.current.mx,
        y: dragOriginRef.current.oy + ev.clientY - dragOriginRef.current.my
      });
    }

    function onUp() {
      dragOriginRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  function isVisible(action: Action) {
    if (action === "netsuiteAI") return !!(subscription?.enableNetsuite && subscription?.enableAI);
    if (action === "dynamicsAI") return !!(enableDynamics && subscription?.enableAI);
    if (action === "hubSpotAI") return !!(enableHubSpot && subscription?.enableAI);
    if (action === "bigCommerceAI") return !!(enableBigCommerce && subscription?.enableAI);
    // aiComposeEmail: always show — editor displays upgrade prompt if not subscribed
    return true;
  }

  function isNewFeature(action: Action) {
    return action === "aiComposeEmail";
  }

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
      className="w-72 bg-white rounded-xl shadow-2xl border border-slate-200 cursor-default overflow-hidden select-none"
    >
      {/* Drag handle / header */}
      <div
        onMouseDown={handleDragHandleMouseDown}
        className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 cursor-grab active:cursor-grabbing"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-slate-400 flex-shrink-0" aria-hidden="true">
          {[0, 4, 8].map(y =>
            [0, 4].map(x => (
              <circle key={`${x}-${y}`} cx={x + 2} cy={y + 2} r="1.2" fill="currentColor" />
            ))
          )}
        </svg>
        <span className="text-xs font-semibold text-slate-600 flex-1">Add Action</span>
      </div>

      {/* Grouped action list */}
      <div className="overflow-y-auto" style={{ maxHeight: "min(480px, calc(100vh - 160px))" }}>
        {ACTION_GROUPS.map((group) => {
          const visible = group.actions.filter(isVisible);
          if (visible.length === 0) return null;
          return (
            <div key={group.label}>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">
                {group.label}
              </div>
              {visible.map((action) => {
                const def = actionDefs[action];
                if (!def) return null;
                const ActionIcon = def.icon;
                return (
                  <div
                    key={action}
                    onClick={() => onSelect(action)}
                    className="group flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-cornflower-blue-50 border-b border-slate-50 last:border-b-0"
                  >
                    <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md transition-colors ${isNewFeature(action) ? "bg-violet-100 group-hover:bg-violet-200" : "bg-slate-100 group-hover:bg-cornflower-blue-100"}`}>
                      <ActionIcon className={`w-4 h-4 transition-colors ${isNewFeature(action) ? "text-violet-600" : "text-slate-500 group-hover:text-cornflower-blue-500"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="text-xs font-medium text-slate-700 group-hover:text-cornflower-blue-700 transition-colors">
                          {def.name}
                        </div>
                        {isNewFeature(action) && (
                          <span className="text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 px-1 py-0.5 rounded">New</span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400 leading-tight truncate">
                        {def.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NodeActions({ children }: { children: React.ReactNode }) {
  return (
    <>
      <div className="absolute left-0 -bottom-12 flex gap-2">
        {children}
      </div>
    </>
  );
}

function DragDots() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <g clipPath="url(#clip0_2651_89045)">
        <path
          d="M8 5C8 5.26522 8.10536 5.51957 8.29289 5.70711C8.48043 5.89464 8.73478 6 9 6C9.26522 6 9.51957 5.89464 9.70711 5.70711C9.89464 5.51957 10 5.26522 10 5C10 4.73478 9.89464 4.48043 9.70711 4.29289C9.51957 4.10536 9.26522 4 9 4C8.73478 4 8.48043 4.10536 8.29289 4.29289C8.10536 4.48043 8 4.73478 8 5Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 12C8 12.2652 8.10536 12.5196 8.29289 12.7071C8.48043 12.8946 8.73478 13 9 13C9.26522 13 9.51957 12.8946 9.70711 12.7071C9.89464 12.5196 10 12.2652 10 12C10 11.7348 9.89464 11.4804 9.70711 11.2929C9.51957 11.1054 9.26522 11 9 11C8.73478 11 8.48043 11.1054 8.29289 11.2929C8.10536 11.4804 8 11.7348 8 12Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 19C8 19.2652 8.10536 19.5196 8.29289 19.7071C8.48043 19.8946 8.73478 20 9 20C9.26522 20 9.51957 19.8946 9.70711 19.7071C9.89464 19.5196 10 19.2652 10 19C10 18.7348 9.89464 18.4804 9.70711 18.2929C9.51957 18.1054 9.26522 18 9 18C8.73478 18 8.48043 18.1054 8.29289 18.2929C8.10536 18.4804 8 18.7348 8 19Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 5C14 5.26522 14.1054 5.51957 14.2929 5.70711C14.4804 5.89464 14.7348 6 15 6C15.2652 6 15.5196 5.89464 15.7071 5.70711C15.8946 5.51957 16 5.26522 16 5C16 4.73478 15.8946 4.48043 15.7071 4.29289C15.5196 4.10536 15.2652 4 15 4C14.7348 4 14.4804 4.10536 14.2929 4.29289C14.1054 4.48043 14 4.73478 14 5Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 12C14 12.2652 14.1054 12.5196 14.2929 12.7071C14.4804 12.8946 14.7348 13 15 13C15.2652 13 15.5196 12.8946 15.7071 12.7071C15.8946 12.5196 16 12.2652 16 12C16 11.7348 15.8946 11.4804 15.7071 11.2929C15.5196 11.1054 15.2652 11 15 11C14.7348 11 14.4804 11.1054 14.2929 11.2929C14.1054 11.4804 14 11.7348 14 12Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 19C14 19.2652 14.1054 19.5196 14.2929 19.7071C14.4804 19.8946 14.7348 20 15 20C15.2652 20 15.5196 19.8946 15.7071 19.7071C15.8946 19.5196 16 19.2652 16 19C16 18.7348 15.8946 18.4804 15.7071 18.2929C15.5196 18.1054 15.2652 18 15 18C14.7348 18 14.4804 18.1054 14.2929 18.2929C14.1054 18.4804 14 18.7348 14 19Z"
          stroke="#CBD5E1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_2651_89045">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}
